import { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDevice, useSensorData, useProductCapabilities, useFieldPending, useMTCommand } from '@/hooks';
import { deviceService } from '@/api/services';
import { NAMTMessageType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { PendingBadge, ToggleSwitch } from '@/components/common/ConfigControls';
import { findPendingMT, getCombinedAddress } from '@/lib/sensor-commands';
import { formatDateTime } from '@/lib/formatters';
import { resolveAlarmType, resolveArmingMode, resolveOnlineState, onlineStateVariant, alarmTypeIconPath, getZoneSensorLabel } from '@/lib/enum-resolvers';
import { isBuiltInZone, isIntegratedXPulseSensor } from '@/lib/product-capabilities';
import type { NASecurityObject } from '@/api/models';

/**
 * Generic zone-list panel shared by every security-sensor-list category screen
 * (Intrusion Zones, Smoke/CO, Temperature, Misc & Siren, Misc). Mirrors mobile's
 * single reusable SensorPage/SensorPageViewModel, which every DeviceCategory
 * dashboard tile navigates into with a different category filter
 * (SensorPageViewModel.cs:1052: `SensorStateList.Where(e => e.DeviceCategory == SensorUI.CategoryEnum)`).
 * All of these categories share the same underlying `securitySensorlist` records,
 * the same descriptor-edit/pending wiring, and the same on-demand command
 * (GET_SECURITY_DATA) — only the filter predicate and page copy differ.
 */
export function SecurityZoneListPanel({
  title,
  description,
  emptyTitle,
  emptyDescription,
  noZonesTitle,
  noZonesDescription,
  emptyIconPath,
  filterFn,
  showConnectedToggle,
  connectedToggleLabel,
}: {
  title: string;
  description: string;
  emptyTitle: string;
  emptyDescription: string;
  noZonesTitle: string;
  noZonesDescription: string;
  emptyIconPath: string;
  filterFn: (zone: NASecurityObject) => boolean;
  // Mobile's SensorsSettingsPageViewModel shows a "Sensor Connected" toggle
  // only for Security/Electronics/EngineCowling/FloatSwitch zones — not for
  // Smoke/CO/Temperature/Misc/MiscSiren. Callers opt in explicitly rather
  // than this component guessing from its filter.
  showConnectedToggle?: boolean;
  connectedToggleLabel?: string;
}) {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDeviceInfo, deviceInfoLoading, refreshDeviceInfo } = useDevice();
  const { sensorData } = useSensorData();
  const caps = useProductCapabilities();

  const allZones = useMemo(() => sensorData?.securitySensorlist ?? [], [sensorData]);

  const [refreshing, setRefreshing] = useState(false);

  // Inline descriptor editing state
  const [editingZoneNum, setEditingZoneNum] = useState<number | null>(null);
  const [editingDescriptor, setEditingDescriptor] = useState('');
  const [descriptorSaving, setDescriptorSaving] = useState(false);
  const fieldPending = useFieldPending();
  const { sendCommand, mtObjList } = useMTCommand(deviceUUID ?? '');

  const userZones = useMemo(() => allZones.filter(filterFn), [allZones, filterFn]);

  // Keep the button spinning until the device actually receives the command
  // (or the pending window times out), not just until the enqueue POST
  // returns — the REST call resolves as soon as it's queued, well before the
  // device checks in.
  const refreshPending = refreshing || !!findPendingMT(mtObjList, NAMTMessageType.GET_SECURITY_DATA);

  const handleRefresh = async () => {
    if (!deviceUUID) return;
    setRefreshing(true);
    try {
      await sendCommand(NAMTMessageType.GET_SECURITY_DATA);
    } finally {
      setRefreshing(false);
    }
  };

  const startEditDescriptor = useCallback((zoneNum: number, currentDescriptor: string) => {
    setEditingZoneNum(zoneNum);
    setEditingDescriptor(currentDescriptor);
  }, []);

  const saveDescriptor = useCallback(async () => {
    if (editingZoneNum == null || !deviceUUID) return;
    // Backend's formatMTCommandDescriptorCmd (NAMTObject.cs) requires a 3-element
    // dataStrList: [combinedAddress, zoneNum, descriptor] — matches mobile's
    // SensorsSettingsPageViewModel.sensorDescriptorNameChangedCallback exactly.
    const zoneRecord = allZones.find((z) => z.zoneNum === editingZoneNum);
    if (!zoneRecord) return;
    setDescriptorSaving(true);
    const zoneNum = editingZoneNum;
    const newDescriptor = editingDescriptor;
    try {
      await fieldPending.run(`zone-descriptor-${zoneNum}`, newDescriptor, () =>
        sendCommand(NAMTMessageType.MT_MESSAGE_SET_ZONE_DESCRIPTORS, [
          getCombinedAddress(zoneRecord.sourceAddressMSB, zoneRecord.sourceAddressLSB),
          String(zoneNum),
          newDescriptor,
        ]),
      );
    } finally {
      setDescriptorSaving(false);
      setEditingZoneNum(null);
    }
  }, [editingZoneNum, editingDescriptor, deviceUUID, allZones, sendCommand, fieldPending]);

  const [connectedFeedback, setConnectedFeedback] = useState<string | null>(null);

  // Mobile's sensorConnectedCallback (SensorsSettingsPageViewModel.cs) — a
  // manual, persisted override ("is a real sensor actually wired to this
  // zone slot"), NOT device-reported telemetry (the backend re-stamps the
  // last-saved value onto every incoming report, since firmware never
  // reports it). REST write, not an MT command — no pending-MT tracking
  // applies, just an optimistic value + immediate refetch, same shape as
  // ConfigurationPage's handleSetZoneCategory for the sibling deviceCategory
  // field. Uses the zone's own zoneInstance (not zoneNum - 1) since zoneNum
  // is a device-wide enumeration and zoneInstance is per-physical-sensor —
  // zoneNum - 1 only coincidentally equals zoneInstance for the built-in
  // unit's own zones (see the sixteenth pass in WIRELESS_SENSOR_CONFIG_QA.md).
  const handleToggleConnected = useCallback(
    (zone: NASecurityObject) => {
      if (!deviceUUID) return;
      setConnectedFeedback(null);
      return fieldPending.run(`zone-connected-${zone.zoneNum}`, !zone.isConnected, async () => {
        try {
          await deviceService.setDeviceProperties({
            deviceUUID,
            containedObj: {
              sourceAddressMSB: zone.sourceAddressMSB,
              sourceAddressLSB: zone.sourceAddressLSB,
              zoneInstance: zone.zoneInstance,
              isConnected: !zone.isConnected,
            },
          });
        } catch {
          setConnectedFeedback('Failed to update sensor connected state.');
          return;
        }
        await refreshDeviceInfo();
      });
    },
    [deviceUUID, refreshDeviceInfo, fieldPending],
  );

  if (deviceInfoLoading && !selectedDeviceInfo) {
    return <PageSpinner />;
  }

  if (!selectedDeviceInfo) {
    return (
      <EmptyState
        title="No Device Selected"
        description={`Select a device to view ${title.toLowerCase()}.`}
      />
    );
  }

  if (allZones.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{title}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>
        </div>
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d={emptyIconPath} />
            </svg>
          }
        />
      </div>
    );
  }

  function renderZoneCard(zone: NASecurityObject, idx: number) {
    // Pending descriptor commands carry the new value in their own payload
    // (dataStrList[2]) — same lookup mobile's SensorsSettingsPageViewModel
    // uses to derive its "pendingDescriptor". Fall through to it once the
    // local optimistic value clears (right after the enqueue POST resolves)
    // so the field keeps showing the new value for the whole time the
    // command is actually pending on the device, not just during the POST.
    const pendingDescriptorMT = findPendingMT(
      mtObjList,
      NAMTMessageType.MT_MESSAGE_SET_ZONE_DESCRIPTORS,
      (d) => d[1] === String(zone.zoneNum),
    );
    const zoneDescriptor =
      fieldPending.getOptimisticValue<string>(`zone-descriptor-${zone.zoneNum}`) ??
      pendingDescriptorMT?.dataStrList?.[2] ??
      zone.descriptor;
    const zoneDescriptorPending = fieldPending.isPending(`zone-descriptor-${zone.zoneNum}`) || !!pendingDescriptorMT;
    // Mobile's per-row status chain (SensorPageViewModel.refreshSensorDetails):
    // Red if alarming, else Orange if offline, else Green — same priority order
    // and colors as the dot already used in FloatSwitchPanel/SwitchesPanel, so
    // every app tile now shares one visual status language. Mobile also has a
    // "Disabled" white state with no portal equivalent (nothing here maps to
    // it, and white would be invisible against the card background anyway),
    // and skips the offline check entirely for integrated zones (no online/
    // offline concept for those, matching the online StatusBadge's own gate
    // below).
    const zoneDotColor = zone.alarmPresent
      ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]'
      : !isIntegratedXPulseSensor(zone) && resolveOnlineState(zone.onlineState) === 'Offline'
        ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]'
        : 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]';
    const borderClass = zone.alarmPresent
      ? 'border-red-400 dark:border-red-600'
      : zone.isBypassed
        ? 'border-yellow-400 dark:border-yellow-600'
        : 'border-slate-200 dark:border-navy-700';

    const bgHighlight = zone.alarmPresent
      ? 'bg-red-50 dark:bg-red-900/10'
      : zone.isBypassed
        ? 'bg-yellow-50 dark:bg-yellow-900/10'
        : 'bg-white dark:bg-navy-900';

    return (
      <div
        key={`${zone.zoneNum}-${idx}`}
        className={`rounded-xl border-2 ${borderClass} ${bgHighlight} p-5 transition-colors`}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-4 w-4 shrink-0 rounded-full ${zoneDotColor}`} />
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
              zone.alarmPresent
                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-slate-100 text-slate-500 dark:bg-navy-800 dark:text-slate-400'
            }`}>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={alarmTypeIconPath(zone.alarmType)} />
              </svg>
            </div>
            <div>
              {/* deviceCategory is a coarse hardware classification (mostly
                  UNKNOWN(0) unless explicitly reclassified via the Zone Type
                  Category picker, which only applies to Security-type zones
                  in the first place) — showing it here meant every Smoke/CO/
                  Temperature/Misc/MiscSiren zone displayed "Intrusion"
                  (DEVICE_CATEGORY_MAP[0]'s label), regardless of its actual
                  type. alarmType is the zone's real, specific type (Smoke,
                  CO, Misc, Misc (Siren), etc.) and is always set. */}
              <h3 className="font-semibold text-slate-900 dark:text-white">
                  {zoneDescriptor
                    ? zoneDescriptor.charAt(0).toUpperCase() + zoneDescriptor.slice(1)
                    : resolveAlarmType(zone.alarmType)}
                </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {/* Mobile's "{sensorName}, Zone {N}" (DeviceCategoryMetrics.cs's
                    update*Category methods) — zoneInstance is per-physical-sensor
                    (confirmed against real device data: resets to 0 for each new
                    xPulse), so this identifies which physical sensor a zone belongs
                    to and which of its own 1-4 slots it is, unlike the raw zoneNum
                    (a device-wide enumeration with no sensor-affinity signal). */}
                {getZoneSensorLabel(zone)}, Zone {zone.zoneInstance + 1}
              </p>
            </div>
          </div>
          {/* Don't show online state for integrated XPulse sensors */}
          {!isIntegratedXPulseSensor(zone) && (
            <StatusBadge
              label={resolveOnlineState(zone.onlineState)}
              variant={onlineStateVariant(zone.onlineState)}
              dot
            />
          )}
        </div>

        {/* Status badges */}
        <div className="mb-3 flex flex-wrap gap-2">
          {zone.alarmPresent && (
            <StatusBadge label="ALARM" variant="danger" dot />
          )}
          {zone.isBypassed && (
            <StatusBadge label="Bypassed" variant="warning" dot />
          )}
          {isBuiltInZone(caps, zone) ? (
            <StatusBadge label="Built-in" variant="info" dot />
          ) : (
            // When showConnectedToggle is on, the "Sensor Connected" toggle
            // below already shows this — a static badge here would just
            // repeat it.
            !showConnectedToggle && (
              zone.isConnected ? (
                <StatusBadge label="Connected" variant="success" dot />
              ) : (
                <StatusBadge label="Disconnected" variant="neutral" dot />
              )
            )
          )}
        </div>

        {/* Details */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500 dark:text-slate-400">Arming Mode</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {resolveArmingMode(zone.armingMode)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500 dark:text-slate-400">Type</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {resolveAlarmType(zone.alarmType)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500 dark:text-slate-400">Input Type</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {zone.isNC ? 'Normally Closed' : 'Normally Open'}
            </span>
          </div>
          {showConnectedToggle && (() => {
            const connectedKey = `zone-connected-${zone.zoneNum}`;
            const isConnected =
              fieldPending.getOptimisticValue<boolean>(connectedKey) ?? zone.isConnected;
            const connectedPending = fieldPending.isPending(connectedKey);
            return (
              <div className="flex items-center justify-between pt-1">
                <span className="flex items-center text-slate-500 dark:text-slate-400">
                  {connectedToggleLabel ?? 'Sensor Connected'}
                  {connectedPending && <PendingBadge />}
                </span>
                <ToggleSwitch
                  enabled={!!isConnected}
                  loading={false}
                  onToggle={() => handleToggleConnected(zone)}
                />
              </div>
            );
          })()}
          {zone.updatedDT && (
            <div className="flex justify-between">
              <span className="text-slate-500 dark:text-slate-400">Updated</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {formatDateTime(zone.updatedDT)}
              </span>
            </div>
          )}
          {/* Inline descriptor editor */}
          <div className="flex items-center justify-between pt-1">
            <span className="text-slate-500 dark:text-slate-400">Descriptor</span>
            {editingZoneNum === zone.zoneNum ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  maxLength={10}
                  value={editingDescriptor}
                  onChange={(e) => setEditingDescriptor(e.target.value)}
                  autoFocus
                  className="w-24 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveDescriptor();
                    if (e.key === 'Escape') setEditingZoneNum(null);
                  }}
                />
                <button
                  type="button"
                  onClick={saveDescriptor}
                  disabled={descriptorSaving}
                  className="rounded p-0.5 text-teal-600 hover:bg-teal-50 disabled:opacity-50 dark:text-teal-400 dark:hover:bg-teal-900/20"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setEditingZoneNum(null)}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-navy-700"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {zoneDescriptor || '--'}
                </span>
                <button
                  type="button"
                  onClick={() => startEditDescriptor(zone.zoneNum, zoneDescriptor ?? '')}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-navy-700 dark:hover:text-slate-300"
                  title="Edit descriptor"
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                  </svg>
                </button>
                {zoneDescriptorPending && <PendingBadge />}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{title}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
          On-Demand
        </Button>
      </div>

      {connectedFeedback && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center justify-between">
            <span>{connectedFeedback}</span>
            <button type="button" onClick={() => setConnectedFeedback(null)} className="ml-3 text-xs font-medium underline">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* User zones */}
      {userZones.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {userZones.map((zone, idx) => renderZoneCard(zone, idx))}
        </div>
      )}

      {userZones.length === 0 && (
        <EmptyState
          title={noZonesTitle}
          description={noZonesDescription}
        />
      )}
    </div>
  );
}
